import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Link } from "react-router-dom";
import * as XLSX from "xlsx";
import axios from "axios";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  Button,
  TextField,
  Tabs,
  Tab,
  AppBar,
  Toolbar,
  Backdrop,
  CircularProgress,
  Snackbar, 
  Alert
} from "@mui/material";
import { useDropzone } from "react-dropzone";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DripCampaignEmailGenerator from "./components/DripCampaignEmailGenerator";
import CampaignChatBot from "./components/CampaignChatBot";
import DripScheduleForm from "./components/DripScheduleForm";
import Configuration from "./components/configuration.js";
import TemplateGenerator from "./components/TemplateGenerator";


const API_BASE_URL = process.env.REACT_APP_FASTAPI_URL;

const TARGET_FIELDS = [
    "Source",
    "Product_Interest",
    "Target_Function",
    "Date_Added",
    "Company Name",
    "First Name",
    "Last Name",
    "Job Title",
    "Contact Email",
    "City",
    "Country",
    "Campaign_Status",
    "Email_Status",
    "Last_Contact_Date",
    "Next_Action_Date",
    "Score",
    "Remark"
  ];

function Header() {
    return (
        <AppBar position="static">
          <Toolbar sx={{ justifyContent: "flex-start", gap: 2 }}>
      

              <Typography
                variant="h6"
                sx={{ 
                    fontWeight: "bold",
                    paddingRight: 3
                 }}
              >
                Campaign Bridge
              </Typography>
      
    
            {/* Navigation buttons, still on the left */}
            <Button color="inherit" component={Link} to="/">
              Home
            </Button>
            <Button color="inherit" component={Link} to="/emailGenerator">
              Create Campaign
            </Button>
            <Button color="inherit" component={Link} to="/dripSchedule">
              Drip Campaign Scheduler
            </Button>
            <Button color="inherit" component={Link} to="/campaignChatBot">
          Campaign Chat Bot
        </Button>        
          <Button color="inherit" component={Link} to="/templateGenerator">
         Template Generator
        </Button>
         <Button color="inherit" component={Link} to="/configuration">
          Configuration
        </Button>
          </Toolbar>
        </AppBar>
      );
}

function Footer() {
    return (
      <Box
        component="footer"
        sx={{ textAlign: "center", py: 2, borderTop: "1px solid #ccc", my: 0 , bgcolor: "#eceff1"}}
      >
        © {new Date().getFullYear()} Campaign Bridge. All rights reserved - INNOVAL.
      </Box>
    );
  }
  


function Home() {
// State
const [headers, setHeaders] = useState([]);
const [mapping, setMapping] = useState({});
const [mappingName, setMappingName] = useState("");
const [description, setDescription] = useState("");
const [errors, setErrors] = useState({});
const [mappingsList, setMappingsList] = useState([]);
const [editMappingId, setEditMappingId] = useState(null); // null = create mode
const [showForm, setShowForm] = useState(false);
const [rightTab, setRightTab] = useState("convert");
const [exportFile, setExportFile] = useState(null);

// Loader & Snackbar
const [loading, setLoading] = useState(false);
const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

const {
  getRootProps: getZohoUploadProps,
  getInputProps: getZohoUploadInputProps,
  isDragActive: isZohoUploadDrag,
} = useDropzone({
  accept: ".xlsx, .xls",
  onDrop: (acceptedFiles) => {
    if (acceptedFiles.length > 0) setExportFile(acceptedFiles[0]);
  },
});

const handleExportZoho = async () => {
  if (!exportFile) return alert("Please upload an Excel file.");
  // if (!selectedList) return alert("Please select a Zoho list.");

  const formData = new FormData();
formData.append("file", exportFile);
// formData.append("list_key", selectedList);

setLoading(true);
try {
  const res = await axios.post(`${API_BASE_URL}/zoho/export`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

  setSnackbar({ open: true, message: res?.data?.message || "Exported to Zoho successfully!", severity: "success" });
  setExportFile(null);
} catch (err) {
  setSnackbar({ open: true, message: err.response?.data?.error || "Export failed", severity: "error" });
} finally {
  setLoading(false);
}



};


const handleOutExcelDownload = async () => {
  setLoading(true);
  try {
    const res = await axios.get(`${API_BASE_URL}/excel/download`, {
      responseType: "blob", // important for binary file
    });

    // Extract the filename from Content-Disposition
    const disposition = res.headers["content-disposition"];
    let filename = "output.xlsx"; // fallback
    if (disposition) {
      const match = disposition.match(/filename="?([^"]+)"?/);
      if (match && match[1]) filename = match[1];
    }

    // Create a link to download the blob
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    setSnackbar({
      open: true,
      message: `Downloading ${filename}`,
      severity: "success",
    });
  } catch (err) {
    setSnackbar({
      open: true,
      message: err.response?.data?.error || "Download failed",
      severity: "error",
    });
  } finally {
    setLoading(false);
  }
};


// Fetch existing mappings
useEffect(() => {
  fetchMappings();
}, []);

const fetchMappings = async () => {
  setLoading(true)
  try {
    const res = await axios.get(`${API_BASE_URL}/mappings/`);
    setMappingsList(res.data);
  } 
  catch (err) {
    setSnackbar({ open: true, message: err.response?.data?.error || "Failed to fetch mappings", severity: "error" });
  } finally {
    setLoading(false);
  }
};

// Handle selecting an existing mapping
const handleSelectMapping = (m) => {
setEditMappingId(m.id);
setMappingName(m.name);
setDescription(m.description);
setMapping(m.mapping);
setHeaders([]); // wait for user to upload file
setShowForm(true); // show form
};

// Handle creating new mapping
const handleNewMapping = () => {
setEditMappingId(null);
setMappingName("");
setDescription("");
setMapping({});
setHeaders([]);
setShowForm(true); // show form
};
// Handle save mapping (create/edit)
const handleSave = async () => {
  let newErrors = {};
  if (!mappingName.trim()) newErrors.mappingName = "Mapping Name is required";
  if (!description.trim()) newErrors.description = "Description is required";

  setErrors(newErrors);
  if (Object.keys(newErrors).length > 0) return;


  setLoading(true);
try {
const res = await axios.post(`${API_BASE_URL}/mappings/upsert`, {
  id: editMappingId,
  name: mappingName,
  description,
  mapping,
});
setSnackbar({ open: true, message: res?.data?.message, severity: "success" });
fetchMappings();
setShowForm(false);
setEditMappingId(null);
setMappingName("");
setDescription("");
setMapping({});
} catch (err) {
setSnackbar({ open: true, message: err.response?.data?.error || "Error saving mapping", severity: "error" });
} finally {
setLoading(false);
}
  
};

// Handle file upload for mapping
const handleFileUpload = (file) => {
  const reader = new FileReader();
  reader.onload = (evt) => {
    const bstr = evt.target.result;
    const wb = XLSX.read(bstr, { type: "binary" });
    const ws = wb.Sheets[wb.SheetNames[0]];
    const json = XLSX.utils.sheet_to_json(ws, { header: 1 });
    if (json.length > 0) {
      // Filter out empty or whitespace-only headers
      const headersFiltered = json[0].filter(h => h && h.toString().trim() !== "");
      setHeaders(headersFiltered);
    }
  };
  reader.readAsBinaryString(file);
};

const handleGoBack = () => {
  setShowForm(false);
  fetchMappings()
}

// Dropzone for mapping file
const { getRootProps, getInputProps, isDragActive } = useDropzone({
  accept: ".xlsx, .xls",
  onDrop: (acceptedFiles) => {
    if (acceptedFiles.length > 0) handleFileUpload(acceptedFiles[0]);
  },
});

// Dropzone for final Excel upload
const {
  getRootProps: getUploadProps,
  getInputProps: getUploadInputProps,
  isDragActive: isUploadDrag,
} = useDropzone({
  accept: ".xlsx, .xls",
  onDrop: async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;
    const file = acceptedFiles[0];

    const formData = new FormData();
    formData.append("file", file);

    setLoading(true);
try {
const res = await axios.post(`${API_BASE_URL}/excel/upload-excel`, formData, {
  headers: { "Content-Type": "multipart/form-data" },
});
setSnackbar({ open: true, message: res?.data?.message, severity: "success" });
} catch (err) {
setSnackbar({ open: true, message: err.response?.data?.error || "Upload failed", severity: "error" });
} finally {
setLoading(false);
}

  },
});

const handleMappingChange = (field, value) => {
  setMapping((prev) => ({ ...prev, [field]: value }));
};


return (
  <>

    {/* Main Content */}
    <Box sx={{ display: "flex", flexGrow: 1, gap: 2 ,height: "83.5vh"}}>
      {/* Left Panel */}

      <Paper sx={{ flex: 0.6, display: "flex", flexDirection: "column", p: 2, overflow: "hidden" }}>
<Typography
  variant="h6"
  gutterBottom
  sx={{ borderBottom: "3px solid #1976d2", pb: 1, fontWeight: 600 }}
>
  Excel Field Mapper
</Typography>

{/* Header buttons */}

<Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
{/* Left side: Either "Existing Mappers" or "Go Back" */}
{!showForm ? (
  <Typography
    variant="h6"
    gutterBottom
    sx={{ borderBottom: "3px solid #1976d2", pb: 1, fontWeight: 600 }}
  >
    Existing Mappers
  </Typography>
) : (
  <Button onClick={() => handleGoBack() } variant="outlined" color="primary">
    ← Go Back
  </Button>
)}

{/* Right side: "New Mapping" button only when not in form */}
{!showForm && (
  <Button onClick={handleNewMapping} variant="outlined">
    + New Mapping
  </Button>
)}
</Box>


{/* List view */}
{!showForm && (
  <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
    {mappingsList.map((m) => (
      <Paper
      
        key={m._id || m.id}
        sx={{p: 2, m:1, cursor: "default", display: "flex", flexDirection: "column", boxShadow: "0.8px 3px 5px 0.75px rgba(0, 0, 0, 0.22)", borderRadius:0}}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box>
            <Typography fontWeight={600}>{m.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              -{m.description}
            </Typography>
            <Box sx={{ mt: 1 }}>
            <Typography
component="em"
sx={{ paddingBottom: "4px", display: "inline-block", fontWeight:600, textDecorationLine:"underline" }}
>
Fields Mapped (Target Fields : Mapped Source Fields)
</Typography><br></br>

              <Typography variant="text">
                {Object.entries(m.mapping || {})
                  .map(([key, value]) => `${key}: ${value || "Not mapped"}`)
                  .join(", ")}
              </Typography>
            </Box>
          </Box>
          <Button variant="outlined" size="small" onClick={() => handleSelectMapping(m)}>
            Edit
          </Button>
        </Box>
      </Paper>
    ))}
  </Box>
)}

{/* Form view */}
{showForm && (
  <>
    {/* Mapping Name + Description */}
    <Box sx={{ display: "flex", gap: 2, my: 2 }}>
      <TextField
        label="Mapping Name"
        placeholder="Mapping Name - eg:SourceName ProductName"
        variant="outlined"
        size="small"
        fullWidth
        value={mappingName}
        onChange={(e) => setMappingName(e.target.value)}
        error={!!errors.mappingName}
        helperText={errors.mappingName}
      />
      <TextField
        label="Description"
        variant="outlined"
        size="small"
        fullWidth
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        error={!!errors.description}
        helperText={errors.description}
      />
    </Box>

    {/* Mapping Table */}
    <Box sx={{ flexGrow: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
      <Box sx={{ flexGrow: 1, overflowY: "auto", maxHeight: "calc(100vh - 400px)" }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow sx={{ bgcolor: "#f0f4f8" }}>
              <TableCell sx={{ fontWeight: 600, bgcolor: "#f0f0f0", borderRight: "2px solid white" }}>
                Target Field
              </TableCell>
              <TableCell sx={{ fontWeight: 600, bgcolor: "#f0f0f0" }}>Source Header</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
{TARGET_FIELDS.map((field, i) => (
  <TableRow key={field} sx={{ bgcolor: i % 2 === 0 ? "#ffffff" : "#f9fbfd" }}>
    <TableCell>{field}</TableCell>
    <TableCell>
      {editMappingId ? (
        // Editing mode: all text fields
        <TextField
          fullWidth
          size="small"
          value={mapping[field] || ""}
          placeholder={`Enter ${field}`}
          onChange={(e) => handleMappingChange(field, e.target.value)}
        />
      ) : (
        // Creating mode: use original logic (text field for some, dropdown for others)
        field === "Source" || field === "Product_Interest" || field === "Target_Function" ? (
          <TextField
            fullWidth
            size="small"
            value={mapping[field] || ""}
            placeholder={`Enter ${field}`}
            onChange={(e) => handleMappingChange(field, e.target.value)}
          />
        ) : (
          <Select
            fullWidth
            size="small"
            value={mapping[field] || ""}
            onChange={(e) => handleMappingChange(field, e.target.value)}
            disabled={headers.length === 0}
          >
            <MenuItem value=""><em>-- Select --</em></MenuItem>
            {headers.map((h, idx) => (
              <MenuItem key={idx} value={h}>{h}</MenuItem>
            ))}
          </Select>
        
        
          
        )
      )}
    </TableCell>
  </TableRow>
))}
</TableBody>

        </Table>
      </Box>
    </Box>

    {/* Drag & Drop Zone */}
    {!editMappingId && (
    <Box
      {...getRootProps()}
      sx={{
        border: "2px dashed #90caf9",
        borderRadius: 2,
        p: 2,
        textAlign: "center",
        cursor: "pointer",
        mb: 1,
        mt: 2,
        bgcolor: isDragActive ? "#e3f2fd" : "#fafafa",
        "&:hover": { bgcolor: "#f1faff" },
      }}
    >
      <input {...getInputProps()} />
      <CloudUploadIcon sx={{ fontSize: 40, color: "#1976d2", mb: 1 }} />
      <Typography>{isDragActive ? "Drop the Excel file here…" : "Drag & drop or click to upload mapping file"}</Typography>
    </Box>
    )}

    {/* Save Button */}


    <Button
variant="contained"
sx={{ mt: 2, alignSelf: "flex-end" }}
disabled={
  !editMappingId && headers.length === 0 // only disable if creating AND no headers uploaded
}
onClick={() => {
  handleSave();

}}
>
Save Mapping
</Button>

  </>
)}
</Paper>

      

      {/* Right Panel - Excel Upload */}
      <Paper sx={{ flex: 0.4, display: "flex", flexDirection: "column", p: 2 }}>
<Tabs
  value={rightTab}
  onChange={(e, v) => setRightTab(v)}
  textColor="primary"
  indicatorColor="primary"
  sx={{ borderBottom: "1px solid #ccc", mb: 2 }}
>
  <Tab label="Convert to Excel" value="convert" />
  <Tab label="Export to Zoho" value="zoho" />
</Tabs>

{/* Convert to Excel Tab */}
{rightTab === "convert" && (
  <>
  <Box
    {...getUploadProps()}
    sx={{
      border: "2px dashed #81c784",
      borderRadius: 2,
      p: 4,
      textAlign: "center",
      cursor: "pointer",
      mt: 4,
      mb:1,
      bgcolor: isUploadDrag ? "#e8f5e9" : "#fafafa",
      "&:hover": { bgcolor: "#f4fff6" },
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      height:"70%"
    }}
  >
    <Box>
      <input {...getUploadInputProps()} />
      <CloudUploadIcon sx={{ fontSize: 50, color: "#2e7d32", mb: 1 }} />
      <Typography>
        {isUploadDrag ? "Drop the file to process…" : "Drag & drop or click to upload Excel"}
      </Typography>
    </Box>


  </Box>
      <Button
      variant="contained"
      color="success"
      fullWidth
      onClick={handleOutExcelDownload}
    >
      Download Out Excel
    </Button>
    
  </>
)}

{/* Export to Zoho Tab */}
{rightTab === "zoho" && (
<>
  {/* Upload Box */}
  <Box
    {...getZohoUploadProps()}
    sx={{
      border: "2px dashed #64b5f6",
      borderRadius: 2,
      p: 4,
      textAlign: "center",
      cursor: "pointer",
      mt:4,
      mb:1,
      bgcolor: isZohoUploadDrag ? "#e3f2fd" : "#fafafa",
      "&:hover": { bgcolor: "#f1faff" },
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      height:"70%"
    }}
  >

    <Box>
    <input {...getZohoUploadInputProps()} />
    <CloudUploadIcon sx={{ fontSize: 50, color: "#1976d2", mb: 1 }} />
    <Typography>
      {isZohoUploadDrag
        ? "Drop Excel file for Zoho…"
        : "Drag & drop or click to upload Excel for Zoho"}
    </Typography>
         
    {exportFile && (
      <Typography variant="body2" sx={{ mt: 1 }}>
        Selected: {exportFile.name}
      </Typography>
    )}
    </Box>

  </Box>

 
  {/* Export button remains the same */}
  <Button
    variant="contained"
    color="primary"
    fullWidth
    onClick={handleExportZoho}
  >
    Export to Zoho
  </Button>
</>
)}

</Paper>


    </Box>

    {/* Loader Backdrop */}
<Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
<CircularProgress color="inherit" />
</Backdrop>

{/* Snackbar for messages */}
<Snackbar
open={snackbar.open}
autoHideDuration={4000}
onClose={() => setSnackbar({ ...snackbar, open: false })}
anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
>
<Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: "100%" }}>
  {snackbar.message}
</Alert>
</Snackbar>

  </>
);
}

function MainUi() {
  return (
    <Router>
      <Header />
      <Box sx={{ minHeight: "70vh", p: 2 }}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/emailGenerator" element={<DripCampaignEmailGenerator />} />
          <Route path="/dripSchedule" element={<DripScheduleForm />} />
          <Route path="/campaignChatBot" element={<CampaignChatBot />} />
          <Route path="/configuration" element={<Configuration />} />
          <Route path="/templateGenerator" element={<TemplateGenerator />} />
        </Routes>
      </Box>
      <Footer />
    </Router>
  );
}

export default MainUi;
